import { createStore } from 'vuex';
import {formModule} from './modules/formModule';
import {currencyModule} from './modules/currencyModule';
import {validityCheckModule} from './modules/validityCheckModule';
import ApiClient from '../core/ApiClient';
import { normalizeFlagEntity } from '../utils/flagUrl.js';

function syncLangIdToLocalStorage(lang) {
    if (typeof localStorage === 'undefined' || lang == null || lang.id == null) return;
    localStorage.setItem('langId', String(lang.id));
}

/**
 * Normalizes `/languages` API response into the same shape SSR delivers via
 * `window.__APP_DATA__.languages`. Used as the fallback path when SSR bootstrap
 * is missing or empty.
 */
function normalizeLanguagesApiResponse(res) {
    const payload = res?.data?.data ?? res?.data ?? [];
    if (!Array.isArray(payload)) return [];
    return payload.map((language) => normalizeFlagEntity(language));
}

/** Shared in-flight promise for `loadLanguages` to dedupe concurrent dispatches. */
let loadLanguagesInflight = null;

/**
 * Legacy fallback if the API/SSR payload ever ships a language without
 * `currency_code` filled in. Primary source is `lang.currency_code` (see
 * resolveCurrencyCodeByLanguage below); this map only kicks in for old data.
 */
const LANGUAGE_CURRENCY_MAP = {
    EN: 'EUR',
    NL: 'EUR',
    DE: 'EUR',
    PL: 'PLN',
    IT: 'EUR',
    HU: 'HUF',
    HR: 'EUR',
    SR: 'RSD',
    SV: 'SEK',
    RO: 'RON',
    'RO-MD': 'MDL',
    BG: 'EUR',
    CS: 'CZK'
};

function getLocaleCode(lang) {
    return String(lang?.code ?? lang ?? '')
        .trim()
        .toUpperCase()
        .replace('_', '-');
}

/**
 * Resolves the currency that belongs to a language. The CMS exposes
 * `currency_code` on every language row (see /languages payload) — that is
 * the source of truth. We only fall back to LANGUAGE_CURRENCY_MAP when the
 * field is missing (very old data) so the app never lands without a currency.
 */
function resolveCurrencyCodeByLanguage(lang) {
    const fromLang = String(lang?.currency_code ?? '').trim().toUpperCase();
    if (fromLang) return fromLang;

    const locale = getLocaleCode(lang);
    if (LANGUAGE_CURRENCY_MAP[locale]) return LANGUAGE_CURRENCY_MAP[locale];
    const base = locale.split('-')[0];
    return LANGUAGE_CURRENCY_MAP[base] || LANGUAGE_CURRENCY_MAP.EN;
}

export default createStore({
    modules: {
        form: formModule,
        currency: currencyModule,
        validityCheck: validityCheckModule
    },
    state: {
        project: null,
        page: null,
        texts: {},
        lang: null,
        languages: [],
        currencies: [],
        defPath: '/',
        mobileView: false
    },
    getters: {
        project: state => state.project,
        page: state => state.page,
        texts: state => state.texts,
        lang: state => state.lang,
        languages: state => state.languages,
        currencies: state => state.currencies,
        currency: (state, getters) => getters['currency/currentCurrency'],
        defPath: state => state.defPath,
        mobileView: state => state.mobileView
    },
    mutations: {
        SET_PROJECT(state, project) {
            state.project = project;
        },
        SET_PAGE(state, page) {
            state.page = page;
        },
        SET_TEXTS(state, texts) {
            state.texts = texts;
        },
        SET_LANG(state, lang) {
            state.lang = normalizeFlagEntity(lang);
        },
        SET_LANGUAGES(state, languages) {
            state.languages = Array.isArray(languages)
                ? languages.map((language) => normalizeFlagEntity(language))
                : [];
        },
        SET_CURRENCIES(state, currencies) {
            state.currencies = currencies;
        },
        SET_DEF_PATH(state, defPath) {
            state.defPath = defPath;
        },
        INIT_STATE(state, data) {
            state.project = data.project || null;
            state.page = data.page || null;
            state.texts = data.texts || {};
            state.lang = normalizeFlagEntity(data.lang) || null;
            state.languages = Array.isArray(data.languages)
                ? data.languages.map((language) => normalizeFlagEntity(language))
                : [];
            state.currencies = data.currencies || [];
            state.defPath = data.defPath || '/';
            state.mobileView = data.mobileView || false;
        }
    },
    actions: {
        initState({ commit, dispatch }, data) {
            commit('INIT_STATE', data);
            syncLangIdToLocalStorage(data?.lang);
            /**
             * Seed the form module's countries straight from SSR bootstrap so the
             * first paint does not need GET /countries. `loadInitialData` then
             * skips the fetch when this state is fresh for the current language
             * (see formModule.js → loadInitialData / countriesFetchLanguageId).
             */
            if (Array.isArray(data?.countries) && data.countries.length > 0) {
                commit('form/SET_COUNTRIES', data.countries, { root: true });
                commit(
                    'form/SET_COUNTRIES_FETCH_LANGUAGE_ID',
                    data?.lang?.id ?? null,
                    { root: true }
                );
            }
            if (Array.isArray(data?.productCategories) && data.productCategories.length > 0) {
                commit('form/SET_PRODUCT_CATALOG', data.productCategories, { root: true });
            }
            dispatch('currency/initCurrency', null, { root: true });
        },

        /**
         * Reads SSR-rendered bootstrap from `window.__APP_DATA__` (vendor layout +
         * resources/views/injections/body.blade.php) and seeds the store.
         * Replaces the inline bootstrap that used to live in app.js, so
         * languages/countries/currencies arrive without extra API requests.
         */
        bootstrapFromWindow({ dispatch }) {
            if (typeof window === 'undefined') return null;
            const data = window.__APP_DATA__;
            if (!data) return null;
            dispatch('initState', {
                project: data.project,
                page: data.page,
                texts: data.texts,
                lang: data.lang,
                languages: data.languages,
                countries: data.countries,
                productCategories: data.productCategories,
                currency: data.currency,
                currencies: data.currencies,
                defPath: data.defPath,
                mobileView: data.mobileView,
            });
            return data;
        },

        changeLanguage({ commit, dispatch }, lang) {
            commit('SET_LANG', lang);
            syncLangIdToLocalStorage(lang);
            dispatch('syncCurrencyToLanguage');
        },

        /**
         * Pushes the active language's `currency_code` into the currency module,
         * but only if the user has not manually overridden the currency (the
         * autosync mirror in localStorage diverges from the stored value when
         * they have — see currencyModule.applyLanguageCurrency).
         *
         * Falls back to the legacy hardcoded mapping when the API/SSR payload
         * for a language is missing `currency_code` outright.
         */
        syncCurrencyToLanguage({ state, dispatch }) {
            const applied = dispatch('currency/applyLanguageCurrency', state.lang, { root: true });
            if (applied) return;
            const currencyCode = resolveCurrencyCodeByLanguage(state.lang);
            dispatch('currency/setCurrencyByCode', currencyCode, { root: true });
        },

        /**
         * Returns the cached language list when SSR bootstrap already filled it,
         * otherwise fetches once via GET /languages with inflight de-duplication.
         * Pass `{ force: true }` to bypass the cache.
         */
        loadLanguages({ commit, state }, options = {}) {
            const force = options && options.force === true;
            if (!force && Array.isArray(state.languages) && state.languages.length > 0) {
                return Promise.resolve(state.languages);
            }
            if (loadLanguagesInflight) {
                return loadLanguagesInflight;
            }
            loadLanguagesInflight = (async () => {
                try {
                    const api = new ApiClient();
                    const res = await api.request({ path: '/languages' });
                    const list = normalizeLanguagesApiResponse(res);
                    commit('SET_LANGUAGES', Array.isArray(list) ? list : []);
                    return Array.isArray(list) ? list : [];
                } catch {
                    return Array.isArray(state.languages) ? state.languages : [];
                }
            })().finally(() => {
                loadLanguagesInflight = null;
            });
            return loadLanguagesInflight;
        }
    }
});

